/*
* Broadcast Theme
*
* Use this file to add custom Javascript to Broadcast.  Keeping your custom
* Javascript in this fill will make it easier to update Broadcast. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/


(function() {
  // Add custom code below this line

  // MARCA | 30 Sep 2026 | Delivery Estimator app blocks are product-only. Mirror the message under
  // cart checkout on every page. Product pages fill `.estimator-message--block` directly; elsewhere
  // we fetch the first cart product HTML into a srcdoc frame (X-Frame-Options blocks normal iframes).
  const ESTIMATOR_SELECTOR = '.estimator-message--block';
  const CART_ESTIMATOR_ID = 'cart-delivery-estimator';
  const BASE_SIZE = 12;
  const MIN_SIZE = 9;

  let loadToken = 0;
  let cached = { handle: '', html: '' };
  let inflight = null;

  function fitEstimator(block) {
    if (!block || !block.textContent || !block.textContent.trim()) return;

    const targets = [block, ...block.querySelectorAll('div, span, strong')];
    targets.forEach((node) => node.style.removeProperty('font-size'));

    let size = BASE_SIZE;
    block.style.setProperty('font-size', size + 'px', 'important');

    while (block.scrollWidth > block.clientWidth + 1 && size > MIN_SIZE) {
      size -= 0.5;
      block.style.setProperty('font-size', size + 'px', 'important');
    }

    targets.forEach((node) => {
      if (node !== block) {
        node.style.setProperty('font-size', size + 'px', 'important');
      }
    });
  }

  function cartTarget() {
    return document.getElementById(CART_ESTIMATOR_ID);
  }

  function sourceEstimator() {
    return Array.from(document.querySelectorAll(ESTIMATOR_SELECTOR)).find((el) => {
      return el.id !== CART_ESTIMATOR_ID && el.textContent && el.textContent.trim();
    });
  }

  function flattenEstimatorHtml(html) {
    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    wrap.querySelectorAll('br').forEach((node) => node.replaceWith(document.createTextNode(' ')));
    wrap.querySelectorAll('div, p').forEach((node) => {
      const span = document.createElement('span');
      span.innerHTML = node.innerHTML;
      node.replaceWith(span);
    });
    return wrap.innerHTML.replace(/\s+/g, ' ').trim();
  }

  function applyHtml(html) {
    const target = cartTarget();
    if (!target || !html) return;
    const flat = flattenEstimatorHtml(html);
    if (target.innerHTML !== flat) target.innerHTML = flat;
    fitEstimator(target);
  }

  function clearCartEstimator() {
    const target = cartTarget();
    if (!target) return;
    target.innerHTML = '';
    cached = { handle: '', html: '' };
  }

  function fetchCart() {
    return fetch(window.theme?.routes?.cart_url ? window.theme.routes.cart_url + '.js' : '/cart.js', {
      credentials: 'same-origin',
    }).then((res) => {
      if (!res.ok) throw new Error('cart fetch failed');
      return res.json();
    });
  }

  function loadEstimatorFromProduct(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error('product fetch failed');
        return res.text();
      })
      .then((html) => {
        return new Promise((resolve, reject) => {
          const iframe = document.createElement('iframe');
          iframe.setAttribute('aria-hidden', 'true');
          iframe.tabIndex = -1;
          iframe.style.cssText =
            'position:fixed;left:-9999px;top:0;width:480px;height:900px;opacity:0;pointer-events:none;border:0;';

          let settled = false;
          const finish = (err, value) => {
            if (settled) return;
            settled = true;
            clearInterval(poll);
            clearTimeout(timeout);
            iframe.remove();
            if (err) reject(err);
            else resolve(value);
          };

          const poll = setInterval(() => {
            try {
              const doc = iframe.contentDocument;
              if (!doc) return;
              const el = Array.from(doc.querySelectorAll(ESTIMATOR_SELECTOR)).find(
                (node) => node.id !== CART_ESTIMATOR_ID && node.textContent && node.textContent.trim()
              );
              if (el) finish(null, el.innerHTML);
            } catch (error) {
              finish(error);
            }
          }, 250);

          const timeout = setTimeout(() => finish(new Error('estimator timeout')), 12000);

          // Same-origin product pages send X-Frame-Options: DENY, so load via srcdoc instead.
          const base = '<base href="' + window.location.origin + '/">';
          const srcdoc = /<head[^>]*>/i.test(html)
            ? html.replace(/<head[^>]*>/i, (match) => match + base)
            : base + html;

          document.body.appendChild(iframe);
          iframe.srcdoc = srcdoc;
        });
      });
  }

  async function syncCartEstimator(forceReload) {
    const target = cartTarget();
    if (!target) return;

    const source = sourceEstimator();
    if (source) {
      cached = { handle: cached.handle, html: source.innerHTML };
      applyHtml(source.innerHTML);
      return;
    }

    const token = ++loadToken;

    try {
      const cart = await fetchCart();
      if (token !== loadToken) return;

      if (!cart.items || !cart.items.length) {
        clearCartEstimator();
        return;
      }

      const item = cart.items[0];
      const handle = item.handle;
      const productUrl = (item.url ? item.url.split('?')[0] : '/products/' + handle);

      if (!forceReload && cached.handle === handle && cached.html) {
        applyHtml(cached.html);
        return;
      }

      if (inflight && inflight.handle === handle) {
        const html = await inflight.promise;
        if (token !== loadToken) return;
        cached = { handle, html };
        applyHtml(html);
        return;
      }

      const promise = loadEstimatorFromProduct(productUrl);
      inflight = { handle, promise };

      try {
        const html = await promise;
        if (token !== loadToken) return;
        cached = { handle, html };
        applyHtml(html);
      } finally {
        if (inflight && inflight.handle === handle) inflight = null;
      }
    } catch (error) {
      if (cached.html) applyHtml(cached.html);
    }
  }

  const observer = new MutationObserver(() => {
    window.requestAnimationFrame(() => {
      const source = sourceEstimator();
      if (source) {
        applyHtml(source.innerHTML);
        return;
      }

      const target = cartTarget();
      if (!target || !target.textContent || !target.textContent.trim()) return;

      // App may write into the cart node directly; flatten so it stays one line.
      if (target.querySelector('br, div, p')) {
        applyHtml(target.innerHTML);
        return;
      }

      fitEstimator(target);
    });
  });

  function start() {
    syncCartEstimator(true);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', () => syncCartEstimator(true));
  document.addEventListener('theme:cart-drawer:open', () => syncCartEstimator(true));
  window.addEventListener('resize', () => fitEstimator(cartTarget()));

  if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.cartUpdate) {
    window.subscribe(window.theme.PUB_SUB_EVENTS.cartUpdate, () => {
      requestAnimationFrame(() => syncCartEstimator(true));
    });
  }

  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();
